import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Heading, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { useToggleIndex } from 'packages/common-frontend/hooks/useToggleIndex';
import { dollarNumberWithCommasOrDash } from 'packages/common-frontend/utils';
import { LedgerTransactionWithName } from 'packages/statements/ui/ledgerUtils';

const OutstandingLedgerHeader = () => (
  <Thead>
    <Tr>
      <Th width="15%">Settlement Date</Th>
      <Th width="15%">Capital Provider</Th>
      <Th width="15%">Transaction</Th>
      <Th width="15%" textAlign="center">
        Debit
      </Th>
      <Th width="15%" textAlign="center">
        Credit
      </Th>
      <Th width="20%" textAlign="center">
        Total daily balance
      </Th>
      <Th width="5%" />
    </Tr>
  </Thead>
);

type LedgerTdProps = {
  children: React.ReactNode;
  leftAlign?: boolean;
  hideBorder?: boolean;
};
const LedgerTd = ({ leftAlign, hideBorder, children }: LedgerTdProps) => (
  <Td
    textAlign={leftAlign ? 'left' : 'center'}
    border={hideBorder ? 'none' : undefined}
  >
    {children}
  </Td>
);

type LedgerRowProps = {
  transactions: LedgerTransactionWithName[];
};
const OutstandingLedgerBody = ({ transactions }: LedgerRowProps) => {
  const { activeIndex, toggleIndex } = useToggleIndex();

  return (
    <Tbody>
      {transactions.map(
        (
          {
            postingDate,
            capitalProviderName,
            transactionType,
            transactionName,
            type,
            amount,
            balance,
            dailyBalance,
            comment,
          },
          index,
        ) => {
          const isActiveIndex = activeIndex === index;

          return (
            <React.Fragment
              key={`${postingDate}-${transactionType}-${amount}-${balance}`}
            >
              <Tr
                onClick={() => comment && toggleIndex(index)}
                style={comment ? { cursor: 'pointer' } : { cursor: 'default' }}
              >
                <LedgerTd leftAlign hideBorder={isActiveIndex}>
                  {commonDate.fullDateStrToHumanDateString(postingDate)}
                </LedgerTd>
                <LedgerTd leftAlign hideBorder={isActiveIndex}>
                  {capitalProviderName}
                </LedgerTd>
                <LedgerTd leftAlign hideBorder={isActiveIndex}>
                  {transactionName}
                </LedgerTd>
                <LedgerTd hideBorder={isActiveIndex}>
                  {type === 'debit' ? dollarNumberWithCommasOrDash(amount) : ''}
                </LedgerTd>
                <LedgerTd hideBorder={isActiveIndex}>
                  {type === 'credit'
                    ? dollarNumberWithCommasOrDash(amount)
                    : ''}
                </LedgerTd>
                <LedgerTd hideBorder={isActiveIndex}>
                  {dollarNumberWithCommasOrDash(dailyBalance)}
                </LedgerTd>
                <LedgerTd hideBorder={isActiveIndex}>
                  {isActiveIndex ? (
                    <ChevronUpIcon />
                  ) : (
                    comment && <ChevronDownIcon />
                  )}
                </LedgerTd>
              </Tr>
              {isActiveIndex && (
                <Tr style={{ cursor: 'default' }}>
                  <Td colSpan={5}>
                    <Heading as="h5" size="sm">
                      Notes
                    </Heading>
                    {comment}
                  </Td>
                </Tr>
              )}
            </React.Fragment>
          );
        },
      )}
    </Tbody>
  );
};

export const OutstandingLedgerTable = ({
  transactions,
}: {
  transactions: LedgerTransactionWithName[];
}) => (
  <Table>
    <OutstandingLedgerHeader />
    <OutstandingLedgerBody transactions={transactions} />
  </Table>
);
